import { slugify } from '../utils/slugify';

export interface BlogPostMeta {
  title: string;
  slug: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  category: string;
  categorySlug?: string;
  tags: string[];
}

interface BlogPostModule {
  default: unknown;
  meta?: BlogPostMeta;
}

export async function getBlogPostBySlug(slug: string): Promise<{ meta: BlogPostMeta; Content: unknown } | null> {
  const modules = import.meta.glob<BlogPostModule>('../blog/posts/*.mdx');
  const normalizedSlug = slug.trim().toLowerCase();

  console.log(`[Blog] Looking for slug: ${normalizedSlug}`);

  for (const [path, resolver] of Object.entries(modules)) {
    try {
      const mod = await resolver();
      const fileSlug = path.split('/').pop()?.replace(/\.mdx$/, '') ?? '';
      const metaSlug = mod.meta?.slug || fileSlug;
      const normalizedMetaSlug = metaSlug.trim().toLowerCase();

      console.log(`[Blog] Checking file: ${path} → slug: ${normalizedMetaSlug}`);

      if (normalizedMetaSlug === normalizedSlug) {
        if (!mod.meta) {
          console.warn(`[Blog] Missing meta in file: ${path}`);
          return null;
        }

        const categorySlug = mod.meta.categorySlug || slugify(mod.meta.category || '');
        return {
          meta: {
            ...mod.meta,
            slug: normalizedMetaSlug,
            categorySlug,
          },
          Content: mod.default,
        };
      }
    } catch (err) {
      console.error(`[Blog] Error loading post at ${path}:`, err);
    }
  }

  console.warn(`[Blog] Post not found for slug: ${slug}`);
  return null;
}

export async function getBlogPosts(): Promise<BlogPostMeta[]> {
  const modules = import.meta.glob<BlogPostModule>('../blog/posts/*.mdx');
  const posts: BlogPostMeta[] = [];

  for (const [path, resolver] of Object.entries(modules)) {
    try {
      const mod = await resolver();
      if (!mod.meta) continue;

      const fileSlug = path.split('/').pop()?.replace(/\.mdx$/, '') ?? '';
      const categorySlug = mod.meta.categorySlug || slugify(mod.meta.category || '');

      posts.push({
        ...mod.meta,
        slug: mod.meta.slug || fileSlug,
        categorySlug,
      });
    } catch (err) {
      console.error(`[Blog] Error loading post at ${path}:`, err);
    }
  }

  return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export async function getBlogPostsByCategory(categorySlug: string): Promise<BlogPostMeta[]> {
  const allPosts = await getBlogPosts();
  return allPosts.filter((post) => post.categorySlug === categorySlug);
}

export async function getRelatedPosts(currentSlug: string, category: string): Promise<BlogPostMeta[]> {
  const allPosts = await getBlogPosts();
  return allPosts
    .filter((post) => post.slug !== currentSlug && post.category === category)
    .slice(0, 3);
}
